/* eslint-disable @typescript-eslint/no-explicit-any */
import Dialog from "@mui/material/Dialog";
import dayjs from "dayjs";
import { useLocalStorage } from "hooks/use-local-storage";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { links } from "@/data/links";
import { useAppSelector } from "@/hooks/redux-hooks";
import { useLang } from "@/hooks/use-lang";

import { ArrowCircleIcon, CrossIcon } from "ui/icon/shared";

// for the event dialog, we store the event info in the json file in the public folder
// and we use the json file to determine if the event is during
export const EventDialog = () => {
  const { lang } = useLang();
  const pathname = usePathname() ?? "/";
  const isLoginDialogOpen = useAppSelector((state) => state.loginDialog.isOpen);
  const isSubscribeDialogOpen = useAppSelector(
    (state) => state.subscribeDialog.isOpen
  );
  const isAuthPages = [links.lineBinding, "/auth/"].some((link) =>
    pathname.includes(link)
  );
  const [eventDate, setEventDate] = useLocalStorage<string | null | boolean>(
    "event-date",
    null
  );
  const [isCI] = useLocalStorage("CI", undefined);
  const [eventInfo, setEventInfo] = useState<any>(null);
  const isExpired =
    dayjs().isAfter(dayjs(eventInfo?.endDate).endOf("day")) ||
    dayjs().isBefore(dayjs(eventInfo?.startDate).startOf("day"));

  useEffect(() => {
    // load event info from json
    fetch("/sales-event/dialog.json")
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        setEventInfo(data);
      });
  }, [lang]);

  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    fetch("/sales-event/image.json")
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        setImage(data[lang]);
      });
  }, [lang]);

  const shouldShowDialog = useCallback(() => {
    if (isCI) return false;
    if (!eventInfo) return false;
    if (lang === "en") return false;
    if (isExpired) return false;
    if (isLoginDialogOpen || isSubscribeDialogOpen) return false;
    if (eventDate === dayjs().format("YYYY-MM-DD")) return false;
    if (isAuthPages || eventDate === undefined) return false;
    return true;
  }, [eventInfo, lang, isLoginDialogOpen, isSubscribeDialogOpen, eventDate]);

  const [show, setShow] = useState(false);

  useEffect(() => {
    if (shouldShowDialog()) {
      setShow(true);
      setEventDate(dayjs().format("YYYY-MM-DD"));
    }
  }, [setEventDate]);

  const renderDialogContent = () => (
    <>
      <CrossIcon
        role="button"
        aria-label="close event dialog"
        onClick={() => setShow(false)}
        className="absolute left-3 top-3 h-6 w-6 cursor-pointer rounded-full fill-white p-1 transition-colors hover:bg-white/20 sm:left-4 sm:top-4 sm:h-9 sm:w-9 sm:p-2"
      />
      <Image
        priority
        quality={100}
        src={image ?? ""}
        width={765}
        height={680}
        alt="event dialog"
        placeholder="blur"
        className="unselectable rounded-[15px_15px_0_0] object-contain"
        blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPk8cr7DwAC8wHF8e4zAAAAAABJRU5ErkJggg=="
      />
      <Link
        href={eventInfo?.dialogLink ?? "/"}
        target="_blank"
        onClick={() => {
          setShow(false);
        }}
        className="flex cursor-pointer items-center justify-center gap-2 rounded-[0_0_15px_15px] bg-[#f5da70] py-3 transition-colors hover:bg-[#f2ce40] sm:gap-4 sm:py-4"
      >
        <h3 className="font-medium leading-none text-orange-500 sm:text-2xl">
          {eventInfo?.dialogButtonText?.[lang]}
        </h3>
        <ArrowCircleIcon className="h-[18px] w-[18px] sm:h-auto sm:w-auto [&>circle]:fill-orange-500 [&>path]:fill-[#F5DA70]" />
      </Link>
    </>
  );

  if (!eventInfo) return null;
  return (
    <Dialog
      open={show}
      onClose={() => setShow(false)}
      sx={{
        "& .MuiDialog-paper": {
          margin: "16px",
          backgroundColor: "transparent",
          boxShadow: "none",
          filter: "drop-shadow(0 10px 8px rgb(0 0 0 / 0.12))",
        },
      }}
      className="mx-auto h-full w-full max-w-[500px]"
    >
      {renderDialogContent()}
    </Dialog>
  );
};
